// 直播數位變焦 — 純函式（APP#9，對齊 Vistaflow M4 數位變焦）。
//
// 縮放/平移狀態與夾制邏輯抽出此檔以利單測（DeviceLivePage 只負責綁事件 +
// 套用 CSS transform）。pan 以「相對可平移範圍的比例」[-1,1] 表示，與元素
// 實際像素尺寸解耦：1× 時無可平移空間→pan 恆為 0；放大後往任一邊最多到 ±1
// （剛好把該側邊緣帶進畫面，不會露出黑邊）。

export const MIN_ZOOM = 1;
export const MAX_ZOOM = 4;
export const ZOOM_STEP = 1; // ＋/－ 按鈕每次 1×
export const WHEEL_STEP = 0.25; // 滾輪每刻度

export interface ZoomState {
  zoom: number;
  /** pan 比例 [-1,1]；x>0 = 內容右移（看見左側），y>0 = 內容下移（看見上側）。 */
  x: number;
  y: number;
}

export const IDENTITY: ZoomState = { zoom: MIN_ZOOM, x: 0, y: 0 };

export function clampZoom(z: number): number {
  if (!Number.isFinite(z)) return MIN_ZOOM;
  return Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, z));
}

/** 夾制 pan：1× 時歸零；其餘夾到 [-1,1]。 */
export function clampPan(state: ZoomState): ZoomState {
  const zoom = clampZoom(state.zoom);
  if (zoom <= MIN_ZOOM) return { zoom, x: 0, y: 0 };
  const clamp1 = (n: number) => (Number.isFinite(n) ? Math.min(1, Math.max(-1, n)) : 0);
  return { zoom, x: clamp1(state.x), y: clamp1(state.y) };
}

/** 設定 zoom（保持 pan 比例，回到 1× 時 pan 自動歸零）。 */
export function withZoom(state: ZoomState, nextZoom: number): ZoomState {
  return clampPan({ ...state, zoom: clampZoom(nextZoom) });
}

export function zoomBy(state: ZoomState, delta: number): ZoomState {
  return withZoom(state, state.zoom + delta);
}

/**
 * 拖曳平移。dxFrac/dyFrac = 位移像素 / 元素尺寸（[-1,1] 量級）。
 * 可平移半幅（占元素尺寸）= (zoom-1)/2 → 把像素位移換算成 pan 比例需乘 2/(zoom-1)。
 */
export function pan(state: ZoomState, dxFrac: number, dyFrac: number): ZoomState {
  const zoom = clampZoom(state.zoom);
  if (zoom <= MIN_ZOOM) return { zoom, x: 0, y: 0 };
  const k = 2 / (zoom - 1);
  return clampPan({ zoom, x: state.x + dxFrac * k, y: state.y + dyFrac * k });
}

export function isZoomed(state: ZoomState): boolean {
  return clampZoom(state.zoom) > MIN_ZOOM;
}

/**
 * 轉成 CSS transform 字串。translate% 以元素自身 border-box 為基準（未受 scale
 * 影響），故平移半幅 = (zoom-1)/2 * 100%。1× 回 'none'（不疊 transform）。
 */
export function toTransform(state: ZoomState): string {
  const s = clampPan(state);
  if (s.zoom <= MIN_ZOOM) return 'none';
  const half = ((s.zoom - 1) / 2) * 100;
  const tx = -s.x * half;
  const ty = -s.y * half;
  return `translate(${tx.toFixed(3)}%, ${ty.toFixed(3)}%) scale(${s.zoom})`;
}

export type FitMode = 'contain' | 'cover';

export function nextFit(fit: FitMode): FitMode {
  return fit === 'contain' ? 'cover' : 'contain';
}
